import style from "./AccountSubscription.module.scss";
import AccountHeader from "./AccountHeader";
import { useMemo } from "react";
import { apiService } from "../../api/apiAdapter";
import { formatExpiryDate } from "../../features/userProfile/mySubscriptions";
import { Button } from "../Button/Button";
import { isPWAInstalled } from "../../utils";
import { storeConnectUrl } from "../../authConfig";
import {
  isInFirst14Days,
  isInRenewalPeriod,
  shouldShowRenewButton as shouldShowRenewButtonUtils,
  shouldShowCancelButton as shouldShowCancelButtonUtils,
} from "../../utils/subscriptionUtils";
import useAuth from "../../hooks/useAuth";
import useSalesforce from "../../hooks/useSalesforce";

const TG_IDS = ["151", "168"];
const SalesforceAccountSubscriptions = ({ showHeader = true }) => {
  const {
    authDetails,
    userProfile,
    userProfileError: error,
    userProfileLoading: loading,
    refreshUserProfile,
  } = useAuth();
  const { isSubscribeEnabled } = useSalesforce();
  const profileData = userProfile;
  const contact = profileData?.contact;
  const isChild = !!contact?._tg_parentcontactid_value;

  // const authID = useSelector(selectID);

  const subscriptions = useMemo(() => {
    const all = profileData?.subscriptions ?? [];
    return all.find((sub) => TG_IDS.includes(String(sub.tg_subscriptionid))) ?? {};
  }, [profileData]);
  const expiryDate = subscriptions?.tg_expirydate;
  const isActive = Boolean(expiryDate) && new Date(expiryDate) >= new Date();
  const nextRenewalDate = subscriptions?.nextRenewalDate;
  const startDate = subscriptions?.startdate;
  const cancelDate = subscriptions?.tg_canceldate;
  const isCancelled = Boolean(cancelDate);
  const isSuspended = Boolean(subscriptions?.suspendendedDate);
  const productName =
    subscriptions?.["tg_subscriptionid@OData.Community.Display.V1.FormattedValue"];
  // Determine if renew button should be shown
  const shouldShowRenewButton = useMemo(() => {
    if (!isSubscribeEnabled) return false;
    // Don't show for multi-user subscriptions
    if (subscriptions?.tg_subscriptionid === "168") return false;
    //if suspended date show renew
    if (isSuspended) return true;
    // Don't show for child accounts or PWA users
    if (isChild || isPWAInstalled()) return false;
    return shouldShowRenewButtonUtils(nextRenewalDate, startDate, cancelDate, expiryDate);
  }, [subscriptions, isChild, nextRenewalDate, startDate, isCancelled, cancelDate, expiryDate]);

  // Determine if cancel button should be shown
  const shouldShowCancelButton = useMemo(() => {
    if (!isSubscribeEnabled) return false;
    // Don't show for multi-user subscriptions
    if (subscriptions?.tg_subscriptionid === "168") return false;

    // Don't show for child accounts or PWA users
    if (isChild || isPWAInstalled()) return false;

    //if suspended date hide cancel
    if (isSuspended) return false;
    return shouldShowCancelButtonUtils(nextRenewalDate, startDate, cancelDate, expiryDate);
  }, [
    subscriptions,
    isChild,
    nextRenewalDate,
    startDate,
    isCancelled,
    shouldShowRenewButton,
    expiryDate,
    cancelDate,
    isSuspended,
  ]);
  // console.log("Subscription details:", subscriptions);
  // const fetchUserProfile = async () => {
  //   setLoading(true);
  //   setError(null);

  //   try {
  //     if (!authID?.u?.["signInNames.emailAddress"]) {
  //       throw new Error("User email not available");
  //     }

  //     const email = authID.u["signInNames.emailAddress"];
  //     const response = await apiService.getUserProfile(email);

  //     if (response.success) {
  //       // if today date is 5 feb 2026
  //       // const fetchExpiryDate = "2027-01-23T02:16:17.000+0000";//future date  that is more than one month from now no button to show
  //       // const fetchExpiryDate = "2026-02-05T02:16:17.000+0000"; //today means already expired show subscribe button
  //       // const fetchExpiryDate = "2026-02-08T02:16:17.000+0000"; //expiring after 3 days after show cancel subscription button
  //       // const fetchExpiryDate = "2026-03-05T02:16:17.000+0000"; //expiring after onemonth  show cancel subscription button
  //       // console.log(
  //       //   fetchExpiryDate,
  //       // );
  //       const result = {
  //         ...response.data,
  //         subscriptions: {
  //           ...response.data.subscriptions,
  //           isCancelDateExist: Boolean(response.data.subscriptions?.tg_canceldate),
  //           tg_canceldate: response.data.subscriptions?.tg_canceldate || null,
  //           // isCancelDateExist:  false
  //           // tg_expirydate: fetchExpiryDate
  //           //Boolean(response.data.subscriptions?.tg_canceldate)},
  //         },
  //       };
  //       setProfileData(result);
  //       console.log(result);
  //     } else {
  //       throw new Error(response.message || "Failed to fetch profile data");
  //     }
  //   } catch (err) {
  //     console.error("Profile fetch error:", err);
  //     setError(err.message || "An error occurred while fetching profile data");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   if (authID?.u) {
  //     fetchUserProfile();
  //   } else {
  //     setLoading(false);
  //   }
  // }, [authID]);

  const handleRetry = async () => {
    if (authDetails?.type !== "user") {
      return;
    }

    try {
      await refreshUserProfile();
    } catch (retryError) {
      console.error("Profile fetch error:", retryError);
    }
  };

  const handleRenewClick = () => {
    storeConnectUrl && window.open(`${storeConnectUrl}subscriptions`, "_blank");
  };

  // apiService.updateSubscription({subscriptionId: "12345", isCancel: true});
  const updateCancelSubscription = async () => {
    if (!subscriptions?.entitlementid) {
      console.error("No entitlement ID available for cancellation");
      return;
    }
    const subscriptionId = subscriptions.entitlementid;
    const isWithInFirst14Days = isInFirst14Days(startDate);
    const isWithInRenewalPeriod = isInRenewalPeriod(nextRenewalDate);
    const cancelContext = isWithInFirst14Days
      ? "first14Days"
      : isWithInRenewalPeriod
        ? "renewalPeriod"
        : "other";
    console.log("Initiating cancellation for subscription ID:", subscriptionId);
    if (cancelContext === "other") {
      console.error("Cancellation not allowed in the current state");
      return;
    }
    try {
      const result = await apiService.updateSubscription({
        subscriptionId,
        cancelContext,
        isCancel: true,
      });
      if (result.success) {
        // console.log("Subscription update successful:", result);
        // Optionally, refresh profile data to reflect changes
        if (authDetails?.type !== "user") {
          return;
        }

        try {
          await refreshUserProfile();
        } catch (retryError) {
          console.error("Profile fetch error:", retryError);
        }
      } else {
        console.error("Subscription update failed:", result.message);
      }
    } catch (err) {
      console.error("Error during subscription update:", err);
    }
  };

  const handleCancelPress = () => {
    if (window.confirm("Are you sure you want to cancel your subscription?")) {
      // put logic of debounce so user can't press again until response not received and also show loading state for cancellation button until response received

      updateCancelSubscription();
    } else {
      console.log("Cancellation aborted by user");
    }
  };
  console.log("SAccountSubscritions-Fetched isActive", isActive);
  if (loading) {
    return (
      <div className={style.accountContainer}>
        <AccountHeader headername="Subscriptions" />
        <div className={style.loaderContainer}>
          <div className={style.loader}></div>
          <p>Loading subscription data...</p>
        </div>
      </div>
    );
  }

  if (authDetails?.type !== "user") {
    // Its better to create new subscription and new salesforceInstitutionAccountSubscription page
    // Note if for institution user need to show subscription Summary currently getUserProfile based on email for subscription object
    //InstitutionSubscription object -need to fetch detail of subscription based on entitelmentId which is institutionsubscription ID in salesforce
    // there is no cancellation its having Grace Period End Date, Termination Date
    return (
      <div className={style.accountContainer}>
        {showHeader && <AccountHeader headername="Subscriptions" />}
      </div>
    );
  }

  return (
    <div className={style.accountContainer}>
      {showHeader && <AccountHeader headername="Subscriptions" />}
      <div className={style.subscriptionContainer}>
        <p className={style.title}>Subscription summary</p>

        {error ? (
          <div className={style.errorContainer}>
            <p className={style.errorMessage}>Sorry, we can't find your subscription details!</p>
            <Button onClick={handleRetry} bgColor="black">
              Retry
            </Button>
          </div>
        ) : (
          <div className={style.subscriptionCard}>
            <div className={style.subscriptionRow}>
              <span className={style.subscriptionLabel}>Products:</span>
              <span className={style.subscriptionValue}>
                {productName || "Therapeutic Guidelines"}
              </span>
            </div>

            {expiryDate && (
              <div className={style.subscriptionRow}>
                <span className={style.subscriptionLabel}>Expiry:</span>
                <span className={style.subscriptionValue}>{formatExpiryDate(expiryDate)}</span>
              </div>
            )}

            {isCancelled && cancelDate && (
              <div className={style.subscriptionRow}>
                <span className={style.subscriptionLabel}>Cancelled:</span>
                <span className={style.subscriptionValue}>{formatExpiryDate(cancelDate)}</span>
              </div>
            )}

            <p className={style.subscriptionNote}>
              If you want to purchase/renew an account with more than 1 licence, please contact our
              Sales Team at{" "}
              <a href="mailto:sales@tg.org.au" className={style.emailLink}>
                sales@tg.org.au
              </a>{" "}
              or call us on +61 3 9329 1566
            </p>

            {shouldShowCancelButton && (
              <button type="button" className={style.renewButton} onClick={handleCancelPress}>
                Cancel Subscription
              </button>
            )}

            {shouldShowRenewButton && (
              <button type="button" className={style.renewButton} onClick={handleRenewClick}>
                Subscribe Now
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesforceAccountSubscriptions;
